// src/App.js
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import './App.css';
import MaintenancePage from './components/MaintenancePage';
import axios from 'axios';

export default function App(){
  const [openIds, setOpenIds] = useState(() => new Set());
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ fullName: '', email: '', phone: '' });
  const [leadStatus, setLeadStatus] = useState('idle');
  const [leadMessage, setLeadMessage] = useState('');

  // ====== Maintenance ======
  const isMaintenanceMode = false;
  const [isIpAllowed, setIsIpAllowed] = useState(false);

  useEffect(() => {
    const checkIpRoute = async () => {
      try {
        const response = await axios.get('/api/ip/check-ip');
        if (response.data && response.data.allowed === true) setIsIpAllowed(true);
        else setIsIpAllowed(false);
      } catch (error) {
        console.error('Error checking IP on server:', error);
        setIsIpAllowed(false);
      }
    };

    checkIpRoute();
  }, []);

  const isMaintenanceActive = isMaintenanceMode && !isIpAllowed;

  // ====== Contacts ======
  const emailUrl = 'mailto:oper8@qpcore.com';
  const waUrl = 'https://wa.me/+31615514444';
  const mapsUrl = 'https://maps.app.goo.gl/tWABfrVDc8zXy34A7?g_st=ic';

  // ====== Cover ======
  const coverUrl = '/gallery/1.jpg';

  // ====== Lightbox ======
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [lbOpen, setLbOpen] = useState(false);

  const closeLb = useCallback(() => {
    setLbOpen(false);
    setTimeout(() => {
      setLightboxSrc('');
      setLightboxAlt('');
    }, 220);
  }, []);

  useEffect(() => {
    if (!lbOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeLb();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lbOpen, closeLb]);

  useEffect(() => {
    if (!lbOpen && !isMapPopupOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lbOpen, isMapPopupOpen]);

  const openLb = useCallback((src, alt='') => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
    setLbOpen(true);
  }, []);

  // ====== Tracking ======
  const sendEvent = useCallback(async (eventName, userData = {}) => {
    const payload = JSON.stringify({ eventName, userData });

    try{
      if (navigator.sendBeacon){
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon('/api/track-event', blob);
        return;
      }
    }catch{}

    try{
      await fetch('/api/track-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });
    }catch{}
  }, []);

  const fire = useCallback(async (name) => {
    try { await sendEvent(name); } catch {}
  }, [sendEvent]);

  const onEmailClick = useCallback(() => {
	  fire('Email Tunnel');
	}, [fire]);

	const onWhatsAppClick = useCallback(() => {
	  fire('WhatsApp Tunnel');
	}, [fire]);

  const onMapButtonClick = useCallback(() => {
    setIsMapPopupOpen(true);
    fire('Maps Popup Open');
  }, [fire]);

  const onMapsClick = useCallback(() => {
    fire('Maps Tunnel');
  }, [fire]);

  const closeMapPopup = useCallback(() => {
    setIsMapPopupOpen(false);
  }, []);

  const onLeadChange = useCallback((e) => {
    const { name, value } = e.target;
    setLeadForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const submitLead = useCallback(async (e) => {
    e.preventDefault();

    const payload = {
      fullName: leadForm.fullName.trim(),
      email: leadForm.email.trim(),
      phone: leadForm.phone.trim(),
    };

    if (!payload.fullName || !payload.email || !payload.phone) {
      setLeadStatus('error');
      setLeadMessage('Please fill in your full name, email and phone.');
      return;
    }

    try {
      setLeadStatus('loading');
      setLeadMessage('');

      await axios.post('/api/leads', payload);
      await sendEvent('Lead Form Submit', { email: payload.email, phone: payload.phone });

      setLeadStatus('success');
      setLeadMessage('Thank you. We received your request and will contact you soon.');
      setLeadForm({ fullName: '', email: '', phone: '' });
    } catch (error) {
      console.error('Lead submit error:', error);
      setLeadStatus('error');
      setLeadMessage(error.response?.data?.error || 'Something went wrong. Please try again.');
    }
  }, [leadForm, sendEvent]);

  // ====== Accordions ======
  const toggle = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sections = useMemo(() => ([
    {
      id: 'services',
      title: 'Services',
      meta: 'Recording • Mixing • Mastering • Full production',
      image: '/gallery/1.jpeg',
      imageAlt: 'OPER8 Production studio services',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/services.jpg', 'OPER8 Production studio services')}
              aria-label="Open studio services photo"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Studio services photo">
                <img
                  className="mediaImg"
                  src="/gallery/services.jpg"
                  alt="OPER8 Production studio services"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">No image available</div>
                </div>
                <div className="tapHint" aria-hidden="true">Tap • fullscreen</div>
              </div>
            </button>
          </div>

          <div className="textBlock">
            <p>
              OPER8 Production is a recording and production studio for artists, creators, brands and modern digital projects.
              We work with rap, trap, pop, rock, R&amp;B and contemporary electronic sound.
            </p>
            <p>
              Every session is built around the artist’s style, the energy of the track and the final platform where the music will live.
              The goal is not just to record audio, but to shape a finished result with character, balance and impact.
            </p>
          </div>

          <div className="serviceList">
            <div className="serviceItem">
              <div className="serviceTitle">Vocal recording</div>
              <div className="serviceText">
                Professional studio recording with proper acoustics, clean signal chain, guidance during takes and help with selecting the strongest performances.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">Mixing</div>
              <div className="serviceText">
                Vocal treatment, beat balance, space, depth, stereo image and density for a modern sound that translates well across Spotify, Apple Music and YouTube.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">Mastering</div>
              <div className="serviceText">
                Final loudness, clarity and competitive polish prepared for streaming platforms and digital release standards.
              </div>
            </div>

            <div className="serviceItem">
              <div className="serviceTitle">Full production support</div>
              <div className="serviceText">
                You can book a single service or combine recording, mixing and mastering into one complete production workflow.
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: 'pricing',
      title: 'Pricing & collaboration',
      meta: 'Rates • packages • location',
      image: '/gallery/1.jpg',
      imageAlt: 'OPER8 Production pricing and collaboration',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/prices.jpg', 'OPER8 Production pricing and collaboration')}
              aria-label="Open pricing photo"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Pricing photo">
                <img
                  className="mediaImg"
                  src="/gallery/prices.jpg"
                  alt="OPER8 Production pricing and collaboration"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">No image available</div>
                </div>
                <div className="tapHint" aria-hidden="true">Tap • fullscreen</div>
              </div>
            </button>
          </div>

          <div className="textBlock">
            <p>
              We work with both new and experienced artists. You can order one specific stage or book a full package for a more streamlined production process.
            </p>
            <p>
              Send a message with your task, genre, references and timeline. We will reply with the most suitable format and details for the session.
            </p>
          </div>

          <div className="priceList">
            <div className="priceRow">
              <div className="priceName">€60 — 1 hour vocal recording / instrument recording</div>
            </div>
            <div className="priceRow">
              <div className="priceName">€60 — simple track mixing (1 hour)</div>
            </div>
            <div className="priceRow">
              <div className="priceName">€120 — medium track mixing (2 hours)</div>
            </div>
            <div className="priceRow">
              <div className="priceName">€60 — mastering according to professional sound standards</div>
            </div>
            <div className="priceRow">
              <div className="priceName">€60 — simple track revisions (1 hour)</div>
            </div>
            <div className="priceRow">
              <div className="priceName">€120 — medium track revisions (2 hours)</div>
            </div>
            <div className="priceRow priceRowStrong">
              <div className="priceName">€270 — recording (2h) + mixing (2h) + 1 revision package (1 free hour)</div>
              <div className="priceSub">Package benefit: save €30 on revisions.</div>
            </div>
          </div>

          <div className="locationCard">
            <div className="serviceTitle">Studio location</div>
            <div className="serviceText">Oostzijde 381, 1508 EP Zaandam, Netherlands</div>
            <a
              className="youtubeLink"
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              onClick={onMapsClick}
            >
              Open in Maps
              <span className="linkArrow" aria-hidden="true">↗</span>
            </a>
          </div>
        </>
      ),
    },
    {
      id: 'legal',
      title: 'Privacy & terms',
      meta: 'Basic website policy • booking terms',
      image: '/gallery/1.jpg',
      imageAlt: 'OPER8 Production privacy and terms',
      body: (
        <>
          <div className="mediaCard">
            <button
              type="button"
              className="mediaCard mediaCardBtn"
              onClick={() => openLb('/gallery/privacy.jpg', 'OPER8 Production privacy and terms')}
              aria-label="Open privacy and terms photo"
            >
              <div className="mediaFrame mediaFrame11" aria-label="Privacy and terms photo">
                <img
                  className="mediaImg"
                  src="/gallery/privacy.jpg"
                  alt="OPER8 Production privacy and terms"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const phEl = e.currentTarget.parentElement?.querySelector('.mediaPlaceholder');
                    if (phEl) phEl.style.display = 'grid';
                  }}
                />
                <div className="mediaPlaceholder" style={{ display: 'none' }}>
                  <div className="placeholderText">No image available</div>
                </div>
                <div className="tapHint" aria-hidden="true">Tap • fullscreen</div>
              </div>
            </button>
          </div>

          <div className="legalBlock">
            <div className="serviceTitle">Privacy policy</div>
            <p>
              This website may use technical logs, basic analytics and direct contact actions in order to operate correctly, prevent abuse and measure communication events such as messenger or email clicks.
            </p>
            <p>
              If you contact OPER8 Production, the information you voluntarily provide may be used only for communication, booking coordination, project discussion and delivery of the requested service.
            </p>
            <p>
              We do not sell personal information to third parties. Technical data may be processed only to maintain website functionality, security and service communication.
            </p>
          </div>

          <div className="legalBlock">
            <div className="serviceTitle">Terms of service</div>
            <p>
              By contacting or booking a service, you agree that project scope, timing, revisions, file delivery and payment terms are discussed individually depending on the type of work.
            </p>
            <p>
              Rates shown on this page are standard reference prices. Final pricing may depend on session length, project complexity, turnaround time and additional production requirements.
            </p>
            <p>
              Recording, mixing, mastering and revision services begin after confirming the task and schedule. Delivery times may vary depending on workload and project details.
            </p>
            <p>
              If you need a custom package, a larger production workflow or a long-term collaboration, please contact us directly before booking.
            </p>
          </div>
        </>
      ),
    },
  ]), [openLb, mapsUrl, onMapsClick]);

  if (isMaintenanceActive) return <MaintenancePage />;

  return (
    <div className="page">
      <header className="hero">
        <div className="coverCol">
          <div className="coverFrame" aria-label="OPER8 Production cover">
            <img
              className="coverImg"
              src={coverUrl}
              alt="OPER8 Production cover"
              loading="eager"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const ph = e.currentTarget.parentElement?.querySelector('.coverPlaceholder');
                if (ph) ph.style.display = 'grid';
              }}
            />
            <div className="coverPlaceholder" style={{ display: 'none' }}>
              <div className="placeholderText">No image available</div>
            </div>

            <div className="coverGlow" aria-hidden="true" />
            <div className="coverScrim" aria-hidden="true" />
          </div>

          <div className="miniBadges">
            <span className="badge">Recording studio</span>
            <span className="badgeStrong">Mixing &amp; mastering</span>
            <span className="badge">Zaandam, Netherlands</span>
          </div>
        </div>

        <div className="infoCol">
          <h1 className="title">OPER8 Production</h1>

          <p className="subtitle">
            Recording and production studio for artists, bloggers, brands and modern digital sound.
          </p>

          <div className="textBlock heroTextBlock">
            <p>
              Recording, mixing and mastering with a clean workflow, focused direction and a finished sound for release.
            </p>
            <p>
              Book a single session or a full production package. Send your contacts and we will discuss the best format.
            </p>
          </div>

          <div className="ctaCard" id="collab">
            <div className="ctaTitle">Collaboration</div>
            <div className="ctaText">
              Contact us in the most convenient way and describe your project. We will reply with the best format, timing and conditions.
            </div>

            <div className="ctaButtons ctaButtonsThree">
              <a
                className="btn btnPrimary"
                href={emailUrl}
                onClick={onEmailClick}
              >
                <IconMail className="btnIcon" />
                <span className="btnLabel">Email</span>
              </a>

              <a
                className="btn btnGhost"
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                onClick={onWhatsAppClick}
              >
                <IconWhatsApp className="btnIcon" />
                <span className="btnLabel">WhatsApp</span>
              </a>

              <button
                type="button"
                className="btn btnGhost"
                onClick={onMapButtonClick}
              >
                <IconMap className="btnIcon" />
                <span className="btnLabel">Map</span>
              </button>
            </div>

            <form className="leadForm" onSubmit={submitLead}>
              <div className="leadFormTitle">Request a session</div>

              <label className="field">
                <span>Full name</span>
                <input
                  name="fullName"
                  type="text"
                  value={leadForm.fullName}
                  onChange={onLeadChange}
                  placeholder="Your full name"
                  autoComplete="name"
                />
              </label>

              <label className="field">
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  value={leadForm.email}
                  onChange={onLeadChange}
                  placeholder="name@example.com"
                  autoComplete="email"
                />
              </label>

              <label className="field">
                <span>Phone</span>
                <input
                  name="phone"
                  type="tel"
                  value={leadForm.phone}
                  onChange={onLeadChange}
                  placeholder="+31 ..."
                  autoComplete="tel"
                />
              </label>

              <button
                type="submit"
                className="btn btnPrimary leadSubmit"
                disabled={leadStatus === 'loading'}
              >
                {leadStatus === 'loading' ? 'Sending...' : 'Send request'}
              </button>

              {leadMessage ? (
                <div className={`leadMessage ${leadStatus === 'success' ? 'leadMessageSuccess' : 'leadMessageError'}`}>
                  {leadMessage}
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </header>

      <main className="main" id="studio">
        <div className="sectionHead">
          <h2 className="sectionTitle">Studio information</h2>
          <div className="sectionHint">All sections are collapsed by default — open them one by one.</div>
        </div>

        <div className="list">
          {sections.map((s) => {
            const isOpen = openIds.has(s.id);

            return (
              <section key={s.id} className="card">
                <button
                  type="button"
                  className="cardHeader"
                  onClick={() => toggle(s.id)}
                  aria-expanded={isOpen ? 'true' : 'false'}
                >
                  <div className="cardHeaderLeft">
                    <div className="cardTitleRow">
                      <div className="cardTitle">{s.title}</div>
                      <div className="cardBadge">{s.meta}</div>
                    </div>
                    <div className="cardMeta">Click to expand</div>
                  </div>

                  <span className={`pmWrap ${isOpen ? 'pmOpen' : ''}`} aria-hidden="true">
                    <span className="pm" />
                  </span>
                </button>

                <div className={`cardBody ${isOpen ? 'cardBodyOpen' : ''}`}>
                  <div className="cardInner">
                    {s.body}
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <footer className="footerCta">
          <div className="ctaTitle">Ready to discuss your track or session?</div>
          <div className="ctaText">
            Send a message by email or WhatsApp and briefly describe the task, references and preferred timing.
          </div>

          <div className="ctaButtons ctaButtonsThree">
            <a
              className="btn btnPrimary"
              href={emailUrl}
              onClick={onEmailClick}
            >
              <IconMail className="btnIcon" />
              <span className="btnLabel">Email</span>
            </a>

            <a
              className="btn btnGhost"
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              onClick={onWhatsAppClick}
            >
              <IconWhatsApp className="btnIcon" />
              <span className="btnLabel">WhatsApp</span>
            </a>

            <button
              type="button"
              className="btn btnGhost"
              onClick={onMapButtonClick}
            >
              <IconMap className="btnIcon" />
              <span className="btnLabel">Map</span>
            </button>
          </div>
        </footer>
      </main>

      <div
        className={`popupOverlay ${isMapPopupOpen ? 'popupOverlayOpen' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Studio location"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeMapPopup();
        }}
        onTouchStart={(e) => {
          if (e.target === e.currentTarget) closeMapPopup();
        }}
      >
        <div className="popupCard">
          <button type="button" className="popupClose" onClick={closeMapPopup} aria-label="Close location popup">
            <svg className="lbCloseIcon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8" />
            </svg>
          </button>

          <div className="popupKicker">Studio location</div>
          <div className="popupTitle">OPER8 Production</div>
          <div className="popupText">Oostzijde 381, 1508 EP Zaandam, Netherlands</div>

          <a
            className="btn btnPrimary popupAction"
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onMapsClick}
          >
            <IconMap className="btnIcon" />
            <span className="btnLabel">Open in Maps</span>
          </a>
        </div>
      </div>

      <div
        className={`lb ${lbOpen ? 'lbOpen' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Photo preview"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeLb();
        }}
        onTouchStart={(e) => {
          if (e.target === e.currentTarget) closeLb();
        }}
      >
        <div className="lbInner">
          <div className="lbFrame">
            <button type="button" className="lbClose" onClick={closeLb} aria-label="Close">
              <svg className="lbCloseIcon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8" />
              </svg>
            </button>

            {lightboxSrc ? (
              <img
                className="lbImg"
                src={lightboxSrc}
                alt={lightboxAlt || 'Photo'}
                draggable="false"
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}


function IconMap({ className = '' }){
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.5A7.2 7.2 0 0 0 4.8 9.7c0 5.1 6.2 11.2 6.5 11.5.4.4 1 .4 1.4 0 .3-.3 6.5-6.4 6.5-11.5A7.2 7.2 0 0 0 12 2.5Zm0 16.5c-1.9-2-5.2-6.2-5.2-9.3A5.2 5.2 0 0 1 12 4.5a5.2 5.2 0 0 1 5.2 5.2c0 3.1-3.3 7.3-5.2 9.3Z" />
      <path d="M12 7a2.8 2.8 0 1 0 0 5.6A2.8 2.8 0 0 0 12 7Zm0 3.7a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8Z" />
    </svg>
  );
}

function IconMail({ className = '' }){
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5v11A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5v-11Zm2 .2v.1l6.5 4.9c.3.2.7.2 1 0L19 6.8v-.1a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5Zm14 2.6-5.3 4c-1 .8-2.4.8-3.4 0L5 9.3v8.2c0 .3.2.5.5.5h13c.3 0 .5-.2.5-.5V9.3Z" />
    </svg>
  );
}

function IconWhatsApp({ className = '' }){
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a9.7 9.7 0 0 0-8.4 14.6L2.6 22l5.6-1.5A9.7 9.7 0 1 0 12 2Zm0 17.7c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.3.9.9-3.2-.2-.3A7.7 7.7 0 1 1 12 19.7Z"/>
      <path d="M16.6 13.7c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.2l-.8 1c-.2.3-.4.3-.7.1-1.7-.8-2.8-2.1-3.2-2.8-.1-.3 0-.5.1-.6l.6-.7c.2-.2.2-.4.3-.6 0-.2 0-.4-.1-.6l-.9-2.1c-.2-.5-.5-.4-.7-.4h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9 0 1.7 1.2 3.4 1.4 3.6.2.2 2.4 3.6 5.8 5 .8.3 1.5.5 2 .6.8.3 1.6.2 2.2.1.7-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.1-1.4 0-.1-.2-.2-.5-.3Z"/>
    </svg>
  );
}

